import React from 'react';

import Pill, { DefaultPillStyles } from 'common/common/Pill';
import IntegrationsLandingHelmet from 'common/helmets/IntegrationsLandingHelmet';
import IntegrationBox from 'common/integrations/IntegrationBox';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import ClickUpLogo from 'img/landing/clickup-logo.webp';
import DevOpsLogo from 'img/landing/devops-logo.webp';
import GistLogo from 'img/landing/gist-logo.webp';
import GoogleAnalyticsLogo from 'img/landing/google-analytics-logo.webp';
import HubSpotLogo from 'img/landing/hubspot-logo.webp';
import IntegratelyLogo from 'img/landing/integrately-logo.webp';
import LinearLogo from 'img/landing/linear-logo.webp';
import MakeLogo from 'img/landing/make-logo.webp';
import TeamsLogo from 'img/landing/microsoft-teams-logo.webp';
import OktaLogo from 'img/landing/okta-logo.webp';
import OneLoginLogo from 'img/landing/onelogin-logo.webp';
import PabblyLogo from 'img/landing/pabbly-logo.webp';
import SegmentLogo from 'img/landing/segment-logo.webp';

import AsanaLogo from 'img/asana-logo.png';
import AzureLogo from 'img/azure-logo-icon.png';
import ChromeLogo from 'img/chrome-logo.svg';
import DiscordLogo from 'img/discord-logo.png';
import GoogleLogo from 'img/google-icon.svg';
import GitHubLogo from 'img/landing/github-logo.svg';
import IntercomLogo from 'img/landing/intercom-logo.svg';
import JiraLogo from 'img/landing/jira-logo.svg';
import SalesforceLogo from 'img/landing/salesforce-logo.svg';
import SlackLogo from 'img/landing/slack-logo.svg';
import SureTriggersLogo from 'img/landing/suretriggers-logo.svg';
import ZapierLogo from 'img/landing/zapier-logo.svg';
import ZendeskLogo from 'img/landing/zendesk-logo.svg';
import OIDCLogo from 'img/open-id-connect-logo.png';

import 'css/components/www/_LandingDirectory.scss';

const IntegrationsLanding = () => {
  return (
    <div className="landingDirectory">
      <IntegrationsLandingHelmet />
      <div className="landingDirectoryContainer">
        <div className="header">
          <div className="left">
            <div className="text">
              <h1 className="title">Canny Integrations</h1>
              <h6 className="subtitle">Connect Canny with products your team already&nbsp;uses.</h6>
            </div>
          </div>
        </div>
        <h2 className="sectionHeader">Project Management Tools</h2>
        <ul className="directoryGrid">
          <IntegrationBox
            title="Jira"
            url="/integrations/jira"
            description="Push Canny posts to Jira, link them to existing Jira issues, and sync statuses
            between. Only for Jira&nbsp;Cloud."
            image={<LazyLoadedImage loading="eager" src={JiraLogo} alt="Jira logo" />}
          />
          <IntegrationBox
            title="GitHub"
            url="/integrations/github"
            description="Push Canny posts to GitHub, link them to existing GitHub issues, and sync
            statuses&nbsp;between."
            image={<LazyLoadedImage loading="eager" src={GitHubLogo} alt="GitHub logo" />}
          />
          <IntegrationBox
            title="ClickUp"
            url="/integrations/clickup"
            description="Push Canny posts to ClickUp, link them to existing ClickUp tasks, and sync
            statuses&nbsp;between."
            image={<LazyLoadedImage loading="eager" src={ClickUpLogo} alt="ClickUp logo" />}
          />
          <IntegrationBox
            title="Linear"
            external
            url="https://linear.app/integrations/canny"
            description="Push Canny posts to Linear, link them to existing Linear issues, and sync
            statuses&nbsp;between."
            image={<LazyLoadedImage loading="eager" src={LinearLogo} alt="Linear logo" />}
          />
          <IntegrationBox
            title="Azure DevOps"
            url="/integrations/devops"
            description="Push Canny posts to Azure DevOps, link them to existing work items, and sync
            statuses between. Only for Azure DevOps&nbsp;Services."
            image={<LazyLoadedImage loading="eager" src={DevOpsLogo} alt="ClickUp logo" />}
          />
          <IntegrationBox
            title="Asana"
            url="/integrations/asana"
            description="Push Canny posts to Asana, link them to existing tasks, and sync
            statuses&nbsp;between."
            image={<LazyLoadedImage loading="eager" src={AsanaLogo} alt="Asana logo" />}
          />
        </ul>
        <h2 className="sectionHeader">Communication Tools</h2>
        <ul className="directoryGrid">
          <IntegrationBox
            title="Intercom"
            url="/integrations/intercom"
            description="Track the feedback you get in Intercom messenger home and live&nbsp;chat."
            image={<LazyLoadedImage src={IntercomLogo} alt="Intercom logo" />}
          />
          <IntegrationBox
            title="Zendesk"
            url="/integrations/zendesk"
            description="Track feedback from tickets without leaving&nbsp;Zendesk."
            image={<LazyLoadedImage src={ZendeskLogo} alt="Zendesk logo" />}
          />
          <IntegrationBox
            title="Salesforce"
            url="/integrations/salesforce"
            description="Track feedback on behalf of contacts and opportunities in&nbsp;Salesforce."
            image={<LazyLoadedImage src={SalesforceLogo} alt="Salesforce logo" />}
          />
          <IntegrationBox
            title="HubSpot"
            url="/integrations/hubspot"
            description="Track feedback on behalf of contacts and deals in&nbsp;HubSpot."
            image={<LazyLoadedImage src={HubSpotLogo} alt="Hubspot logo" />}
          />
          <IntegrationBox
            title="Slack"
            url="/integrations/slack"
            description="Get instant notifications for posts, comments, vote milestones, and
            status&nbsp;changes."
            image={<LazyLoadedImage src={SlackLogo} alt="Slack logo" />}
          />
          <IntegrationBox
            title="Discord"
            url="/integrations/discord"
            description="Get instant notifications in Discord from specific boards and select&nbsp;events."
            image={<LazyLoadedImage src={DiscordLogo} alt="Discord logo" />}
          />
          <IntegrationBox
            title="Microsoft Teams"
            external
            url="https://appsource.microsoft.com/en-us/product/office/wa200001959"
            description="Get instant notifications for posts, comments, vote milestones, and
            status&nbsp;changes."
            image={<LazyLoadedImage src={TeamsLogo} alt="Microsoft Teams logo" />}
          />
          <IntegrationBox
            title={
              <>
                Gist
                <Pill pillStyle={DefaultPillStyles.canny} className="pill">
                  3rd party integration
                </Pill>
              </>
            }
            external
            url="https://docs.getgist.com/article/436-canny-integration"
            description="Track the feedback you get in Gist Messenger and&nbsp;Inbox."
            image={<LazyLoadedImage src={GistLogo} alt="Gist logo" />}
          />
        </ul>
        <h2 className="sectionHeader">Authentication Tools</h2>
        <ul className="directoryGrid">
          <IntegrationBox
            title="Okta SSO"
            url="/integrations/okta"
            description="Let your team sign in to Canny via your Okta&nbsp;organization."
            image={<LazyLoadedImage src={OktaLogo} alt="Okta logo" />}
          />
          <IntegrationBox
            title="OneLogin SSO and Provisioning"
            url="/integrations/onelogin"
            description="Provide a one-click button for your team in their OneLogin dashboard that will log
            them into&nbsp;Canny."
            image={<LazyLoadedImage src={OneLoginLogo} alt="OneLogin logo" />}
          />
          <IntegrationBox
            title="Google Workspace (G&nbsp;Suite)"
            url="/integrations/google-workspace"
            description="Import groups of users from Google Workspace to track feedback from your&nbsp;team"
            image={<LazyLoadedImage src={GoogleLogo} alt="Google logo" />}
          />
          <IntegrationBox
            title="Azure Active Directory"
            url="/integrations/azure-active-directory"
            description="Collect feedback from your employees in Azure&nbsp;AD"
            image={<LazyLoadedImage src={AzureLogo} alt="Azure logo" />}
          />
          <IntegrationBox
            title="OpenID Connect SSO"
            url="/integrations/openid"
            description="Allow your employees to sign in to Canny via any OIDC&nbsp;provider."
            image={<LazyLoadedImage src={OIDCLogo} alt="OpenID logo" />}
          />
        </ul>
        <h2 className="sectionHeader">Other Tools</h2>
        <ul className="directoryGrid">
          <IntegrationBox
            title="Zapier"
            external
            url="https://zapier.com/apps/canny/integrations"
            description="Easily integrate Canny with 5,000+ other apps on&nbsp;Zapier."
            image={<LazyLoadedImage src={ZapierLogo} alt="Zapier logo" />}
          />
          <IntegrationBox
            title="Segment"
            url="/integrations/segment"
            description="Sync rich user data to better inform your product&nbsp;decisions."
            image={<LazyLoadedImage src={SegmentLogo} alt="Segment logo" />}
          />
          <IntegrationBox
            title="Chrome Extension"
            url="/integrations/chrome"
            description="Use Canny’s Chrome Extension to capture user feedback anywhere on the&nbsp;web."
            image={<LazyLoadedImage src={ChromeLogo} alt="Chrome logo" />}
          />
          <IntegrationBox
            title="Make"
            external
            url="https://www.make.com/en/integrations/canny?utm_source=canny&utm_medium=partner&utm_campaign=canny-partner-program"
            description="Easily integrate Canny with hundreds of other apps on&nbsp;Make."
            image={<LazyLoadedImage src={MakeLogo} alt="Make logo" />}
          />
          <IntegrationBox
            title="Pabbly"
            external
            url="https://www.pabbly.com/connect/integrations/canny/"
            description="Easily integrate Canny with hundreds of other apps on&nbsp;Pabbly."
            image={<LazyLoadedImage src={PabblyLogo} alt="Pabbly logo" />}
          />
          <IntegrationBox
            title="Integrately"
            external
            url="https://integrately.com/integrations/canny"
            description="Easily integrate Canny with hundreds of other apps on&nbsp;Integrately."
            image={<LazyLoadedImage src={IntegratelyLogo} alt="Integrately logo" />}
          />
          <IntegrationBox
            title="SureTriggers"
            external
            url="https://suretriggers.com/integrations/canny"
            description="Easily integrate Canny with hundreds of other apps on&nbsp;SureTriggers."
            image={<LazyLoadedImage src={SureTriggersLogo} alt="SureTriggers logo" />}
          />
          <IntegrationBox
            title="Google Analytics"
            url="/integrations/google-analytics"
            description="Track and analyze the traffic you’re getting in&nbsp;Canny"
            image={<LazyLoadedImage src={GoogleAnalyticsLogo} alt="Google Analytics logo" />}
          />
        </ul>
        <LandingFooterCTA style="solid" />
      </div>
    </div>
  );
};

export default IntegrationsLanding;
